import React, {useCallback, useState} from 'react';
import {tid} from '../../../../../../testUtils';
import {ExUserRegistrationDetails} from 'hemwb-api';
import {FormGroup, FormControl, Validators} from 'react-reactive-form';
import {Form, TextFieldControl, useForm} from '../../../../../core/Form';
import {CheckCircleOutlined, DeleteOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {activateExternalUser, deactivateExternalUser, rejectExternalUser} from '../../../../../../store/user';
import {useDispatch} from 'react-redux';
import {Modal, Button, Typography, Divider, Space, Spin} from 'antd';

export type UserActionDialogProps = {
  user: ExUserRegistrationDetails;
  open: boolean;
  onCancel: () => void;
  action: string;
};

const UserActionDialog: React.FC<UserActionDialogProps> = ({user, open, onCancel, action}) => {
  const dispatch = useDispatch();
  const form = useForm(
    new FormGroup({
      reason: new FormControl(null, [Validators.required]),
    }),
  );

  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);

    try {
      if (action === 'activate') {
        await activateExternalUser(dispatch, user.email);
        dispatch(messageAdd('External user has been activated successfully.', MessageTypes.SUCCESS));
        onCancel();
      } else if (action === 'deactivate') {
        await deactivateExternalUser(dispatch, user.email);
        dispatch(messageAdd('External user has now been deactivated successfully.', MessageTypes.SUCCESS));
        onCancel();
      } else {
        await rejectExternalUser(dispatch, user.email, form.controls.reason.value);
        dispatch(messageAdd('External User access request has been rejected.', MessageTypes.SUCCESS));
        onCancel();
      }
    } catch (e) {
      // Handle errors
      if (action === 'activate') {
        dispatch(messageAdd('Failed to activate external user', MessageTypes.ERROR));
      } else if (action === 'deactivate') {
        dispatch(messageAdd('Failed to deactivate external user', MessageTypes.ERROR));
      } else {
        dispatch(messageAdd('Failed to reject request of external user', MessageTypes.ERROR));
      }
    } finally {
      setSubmitting(false);
    }
  }, [action, dispatch, user.email, onCancel, form.controls.reason.value]);

  const getTitle = () => {
    const iconMap = {
      activate: <CheckCircleOutlined style={{color: '#52c41a', marginRight: '8px'}} />,
      deactivate: <ExclamationCircleOutlined style={{color: '#faad14', marginRight: '8px'}} />,
      reject: <DeleteOutlined style={{color: '#ff4d4f', marginRight: '8px'}} />,
    };

    const titleMap = {
      activate: 'Confirm Activation',
      deactivate: 'Confirm Deactivation',
      reject: 'Reject Access Request',
    };

    return (
      <span>
        {iconMap[action as keyof typeof iconMap]}
        {titleMap[action as keyof typeof titleMap]}
      </span>
    );
  };

  const getContent = () => {
    if (action === 'activate') {
      return (
        <Typography.Text>
          By activating the user, they will gain access to the models that have been authorized by the Editor. If you
          have any concerns or issues, please reach out to the Editor for further assistance.
        </Typography.Text>
      );
    } else if (action === 'deactivate') {
      return (
        <Typography.Text>
          Deactivating a user will remove their access to models authorized by the Editor. The Editor can submit a
          request to re-activate the user at any time. Reach out to the Editor for further assistance.
        </Typography.Text>
      );
    } else {
      return (
        <>
          <TextFieldControl
            label="Reason of Rejection"
            name="reason"
            TextFieldProps={{
              inputProps: {
                ...tid('input', 'message'),
              },
            }}
          />
          <Typography.Text style={{marginTop: '12px', display: 'block'}}>
            By rejecting the access request for the user, the user profile will stay inactive. If you have any concerns
            or issues, please reach out to the Editor for further assistance.
          </Typography.Text>
        </>
      );
    }
  };

  const getFooterButtons = (invalid: boolean) => {
    const isRejectOrDeactivate = action === 'reject' || action === 'deactivate';

    return [
      <Button key="cancel" onClick={onCancel} {...tid('dialog-notify-cancel', 'cancel')}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        loading={submitting}
        disabled={(action === 'reject' && invalid) || submitting}
        danger={isRejectOrDeactivate}
        onClick={handleSubmit}
        {...tid('dialog-notify-submit', 'submit')}
      >
        {action === 'activate' && 'Activate User'}
        {action === 'deactivate' && 'Deactivate User'}
        {action === 'reject' && 'Reject Request'}
      </Button>,
    ];
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={onCancel}
      centered
      footer={null}
      {...(tid('dialog-confirm', `${action}-request`) as object)}
      width={500}
    >
      <Spin spinning={submitting}>
        <Form
          group={form}
          render={({invalid}) => (
            <Space direction="vertical" style={{width: '100%'}} size="large">
              <div>
                <Space direction="vertical" size="small" style={{width: '100%'}}>
                  <div>
                    <Typography.Text strong>First Name:</Typography.Text>
                    <Typography.Text> {user.firstName}</Typography.Text>
                  </div>
                  <div>
                    <Typography.Text strong>Last Name:</Typography.Text>
                    <Typography.Text> {user.lastName}</Typography.Text>
                  </div>
                  <div>
                    <Typography.Text strong>Email:</Typography.Text>
                    <Typography.Text> {user.email}</Typography.Text>
                  </div>
                  <div>
                    <Typography.Text strong>Registration Code:</Typography.Text>
                    <Typography.Text> {user.registrationCode}</Typography.Text>
                  </div>
                  <div>
                    <Typography.Text strong>Editor who provided the registration code:</Typography.Text>
                    <Typography.Text> {user.createdBy}</Typography.Text>
                  </div>
                </Space>
              </div>

              <Divider style={{margin: '8px 0'}} />

              <div>{getContent()}</div>

              <Space style={{width: '100%', justifyContent: 'flex-end', float: 'right'}}>
                {getFooterButtons(invalid)}
              </Space>
            </Space>
          )}
        />
      </Spin>
    </Modal>
  );
};

export default UserActionDialog;
